/**
 * useTaskDueNotifications
 *
 * Responsabilidade: Verificar tarefas vencidas/vencendo e disparar notificações.
 * Roda a cada 60 segundos com filtro por papel do usuário.
 *
 * Princípios: Single Responsibility, separação de efeito do contexto principal.
 */

import { useEffect, useRef } from 'react';
import { Task } from '../../types';
import { useNotificationsContext } from '../NotificationsContext';

const WARNING_WINDOW_MS = 15 * 60 * 1000; // 15 minutos
const CHECK_INTERVAL_MS = 60_000; // 1 minuto

interface UseTaskDueNotificationsParams {
    tasks: Task[];
    userRole?: string;
    userId?: string;
}

/**
 * Hook que monitora tarefas e dispara notificações para:
 * - Tarefas vencendo nos próximos 15 minutos
 * - Tarefas já atrasadas
 *
 * Regra de permissão:
 * - member/viewer → apenas tarefas atribuídas ao próprio usuário
 * - owner/admin → todas as tarefas da organização
 */
export function useTaskDueNotifications({ tasks, userRole, userId }: UseTaskDueNotificationsParams): void {
    const { addNotification } = useNotificationsContext();
    const notifiedTasksRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!userRole || !userId) return;

        const checkDueTasks = () => {
            const now = Date.now();

            const filteredTasks = (userRole === 'member' || userRole === 'viewer')
                ? tasks.filter(task => task.assignedTo?.id === userId)
                : tasks;

            for (const task of filteredTasks) {
                if (task.isCompleted || notifiedTasksRef.current.has(task.id)) continue;

                const timeDiff = new Date(task.dueDate).getTime() - now;
                const isOverdue = timeDiff <= 0;
                const isDueSoon = timeDiff > 0 && timeDiff <= WARNING_WINDOW_MS;

                if (isDueSoon || isOverdue) {
                    addNotification({
                        title: isOverdue ? 'Tarefa atrasada!' : 'Tarefa vencendo em breve',
                        message: `"${task.title}" - ${task.lead?.name || 'Sem lead'}`,
                        type: 'task',
                        actionLabel: 'Ver',
                        link: `/tasks?open=${task.id}`,
                        tag: `task-${task.id}`,
                        recipientUserId: task.assignedTo?.id ?? userId,
                        recipientUserName: task.assignedTo?.name,
                    });
                    notifiedTasksRef.current.add(task.id);
                }
            }
        };

        checkDueTasks();
        const intervalId = setInterval(checkDueTasks, CHECK_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [tasks, addNotification, userRole, userId]);
}
